import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
    ActionPanel,
    Box,
    Button,
    Icons,
    Loading,
    Stack,
    Typography,
} from '@thirdspacelearning/library';
import { QUERY_STATUS } from '@tsl-frontend/tsl-utilities';

import ProgrammeInfo from './ProgrammeInfo';
import ProgrammeTable from './ProgrammeTable';

import {
    ProgrammeDeletion,
    ProgrammeForm,
    ProgrammeLOReorder,
    ProgrammeStatus,
} from '../programme-details';
import { ProgrammeLOSelection } from '../programme-los';

import { ProgrammeStatusType, ProgrammeType } from '../../types';

import { getProgramme } from '@api';
import { CurriculumQueryKeys } from '@services';
import { ErrorActionPanel, PageLayout } from '@shared';
import { countryFlags } from '@utils';

import infoSvg from '@assets/info.svg';

const sx = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
    },
    backButton: { mx: 4, alignSelf: 'flex-start' },
    actionPanel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        gap: 3,
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
    },
};

const ProgrammePage = () => {
    const { programmeId } = useParams();

    const [showEditProgramme, setShowEditProgramme] = useState(false);
    const [showDeleteProgramme, setShowDeleteProgramme] = useState(false);
    const [showLOSelection, setShowLOSelection] = useState(false);
    const [showLOReorder, setShowLOReorder] = useState(false);
    const [selectedStatus, setSelectedStatus] =
        useState<ProgrammeStatusType | null>(null);

    const { status, data: programme } = useQuery<ProgrammeType>({
        queryKey: [`${CurriculumQueryKeys.PROGRAMME}-${programmeId}`],
        queryFn: () => getProgramme(programmeId),
        enabled: !!programmeId,
        retry: 1,
        refetchOnWindowFocus: false,
    });

    const isProgrammeLive = programme?.status === ProgrammeStatusType.LIVE;

    return (
        <>
            {status === QUERY_STATUS.pending && (
                <Loading loadingMessage="Loading programme..." />
            )}

            {status === QUERY_STATUS.error && (
                <ErrorActionPanel
                    error="There was an error loading the programme"
                    message="Please refresh the page and try again. If the problem persists, please let us know."
                />
            )}

            {status === QUERY_STATUS.success && programme && (
                <Box sx={sx.root}>
                    <Box display="flex" mb={2} justifyContent="space-between">
                        <Button
                            component={Link}
                            to="/programmes"
                            variant="text"
                            color="primary"
                            startIcon={<Icons.ArrowBackRounded />}
                            sx={sx.backButton}
                        >
                            Return to all programmes
                        </Button>
                        <Box mr={4} display="flex" gap={1}>
                            <Button
                                key="edit-programme"
                                variant="outlined"
                                color="primary"
                                onClick={() => setShowEditProgramme(true)}
                                data-testid="edit-programme-button"
                            >
                                Edit Details
                            </Button>
                            {isProgrammeLive && (
                                <Button
                                    key="delete-programme"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => setShowDeleteProgramme(true)}
                                    data-testid="delete-programme-button"
                                >
                                    Delete Programme
                                </Button>
                            )}
                        </Box>
                    </Box>

                    <PageLayout
                        title={`${countryFlags[programme.country]} ${programme.title}`}
                    >
                        <Stack gap={2}>
                            <ProgrammeInfo
                                programme={programme}
                                setSelectedStatus={setSelectedStatus}
                            />
                            <Box sx={sx.content}>
                                {programme.learning_objectives.length === 0 && (
                                    <Box
                                        sx={sx.actionPanel}
                                        data-testid="no-los-panel"
                                    >
                                        <ActionPanel
                                            buttonAction={() =>
                                                setShowLOSelection(true)
                                            }
                                            buttonLabel="Add learning objective"
                                            decoration={
                                                <img
                                                    src={infoSvg}
                                                    alt="An error occurred"
                                                />
                                            }
                                            title="Start adding learning objectives to the programme"
                                        />
                                    </Box>
                                )}

                                {programme.learning_objectives.length > 0 && (
                                    <>
                                        <Box sx={sx.actions}>
                                            <Typography
                                                fontWeight="bold"
                                                data-testid="los-count"
                                            >
                                                {`${
                                                    programme
                                                        .learning_objectives
                                                        .length
                                                } objective${
                                                    programme
                                                        .learning_objectives
                                                        .length > 1
                                                        ? 's'
                                                        : ''
                                                }`}
                                            </Typography>
                                            <Box display="flex" gap={2}>
                                                {!isProgrammeLive && (
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() =>
                                                            setShowLOSelection(
                                                                true,
                                                            )
                                                        }
                                                        disabled={
                                                            isProgrammeLive
                                                        }
                                                        data-testid="add-los-button"
                                                    >
                                                        Add learning objectives
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() =>
                                                        setShowLOReorder(true)
                                                    }
                                                    data-testid="reorder-los-button"
                                                >
                                                    Reorder objectives
                                                </Button>
                                            </Box>
                                        </Box>

                                        <ProgrammeTable
                                            los={programme.learning_objectives}
                                            programmeId={programme.id}
                                            programmeStatus={programme.status}
                                            country={programme.country}
                                        />
                                    </>
                                )}
                            </Box>
                        </Stack>

                        {/* Edit programme details */}
                        {showEditProgramme && (
                            <ProgrammeForm
                                open={showEditProgramme}
                                onClose={setShowEditProgramme}
                                programme={programme}
                            />
                        )}

                        {/* Edit Status */}
                        {selectedStatus && (
                            <ProgrammeStatus
                                programmeId={programme.id}
                                selectedStatus={selectedStatus}
                                setSelectedStatus={setSelectedStatus}
                                currentStatus={programme.status}
                            />
                        )}

                        {showDeleteProgramme && (
                            <ProgrammeDeletion
                                open={showDeleteProgramme}
                                programmeId={programme.id}
                                programmeTitle={programme.title}
                                setShowDeleteProgramme={setShowDeleteProgramme}
                            />
                        )}

                        {showLOSelection && !isProgrammeLive && (
                            <ProgrammeLOSelection
                                open={showLOSelection}
                                onClose={setShowLOSelection}
                                country={programme.country}
                                programmeId={programme.id}
                                programmeLOs={programme.learning_objectives}
                            />
                        )}

                        {showLOReorder && (
                            <ProgrammeLOReorder
                                open={true}
                                onClose={setShowLOReorder}
                                programmeId={programme.id}
                                programmeLOs={programme.learning_objectives}
                                programmeStatus={programme.status}
                                country={programme.country}
                            />
                        )}
                    </PageLayout>
                </Box>
            )}
        </>
    );
};

export default ProgrammePage;
